import React, { useState } from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import { hot } from 'react-hot-loader';
import { Link, useHistory } from 'react-router-dom';

import { Card, Header, Dimmer, Loader, Breadcrumb, Button, Icon, Segment, Table, Checkbox, Modal, Form, TextArea } from 'semantic-ui-react';

import { withLocale } from 'op/components/locale';
import { withAuditDetails } from './audit-details.helper';

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const formatter = new Intl.DateTimeFormat('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
  return formatter.format(new Date(dateTimeStr));
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return ' ';
  return new Date(dateStr).toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ReadOnlyField = styled.div`
  margin-bottom: 1em;
  flex: 1;
  padding-right: 1em;

  &:last-child {
    padding-right: 0;
  }

  label {
    font-weight: bold;
    display: block;
    margin-bottom: 0.5em;
    color: #333;
  }

  .value {
    padding: 0.7em;
    background: #f9f9f9;
    border-radius: 4px;
    min-height: 2.5em;
    border: 1px solid #e0e0e0;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0.5em;
  gap: 1em;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DownloadSection = styled.div`
  margin-top: 1em;

  .download-item {
    display: flex;
    align-items: center;
    margin-bottom: 0.5em;
    padding: 0.5em;
    background: #f0f0f0;
    border-radius: 4px;

    .filename {
      flex: 1;
      margin-left: 0.5em;
    }
  }
`;

var Thing = styled.div`
  display: flex;
  justify-content: space-between;
`;

var EvalContainer = styled.div`
  display: flex;
`;

var EvalButtonsContainer = styled.div`
  display: flex;
  border-radius: 5px;
  overflow: hidden;
  margin: 0 10px;
`;

var EvalButton = styled.button`
  height: 3em;
  border: none;
  flex: ${props => props.fluid ? 1 : 0};
  border-radius: ${props => props.rounded ? '5px' : 'none'};
  color: ${props => props.selected ? (props.theme && props.theme.white) || '#fff' : (props.theme && props.theme.text) || '#333'};
  background-color: ${props => props.selected ? (props.theme && props.theme.dark) || '#4a4a4a' : (props.theme && props.theme.white) || '#fff'};
  outline: none !important;
  margin: 0 1.5px;
  cursor: default;
`;

const AuditDetails = ({ locale, audit, loading, downloadFile, kpiData, kpiNames, isKPIMng, auditStatuses, mainStatuses, auditPersons, isFollowupMng, auditFollowups, followupTypes }) => {
  var history = useHistory();
  var readOnlyModalState = useState({ open: false, title: '', value: '' });
  var readOnlyModal = readOnlyModalState[0];
  var setReadOnlyModal = readOnlyModalState[1];

  var openReadOnlyModal = function(title, value) {
    setReadOnlyModal({ open: true, title: title, value: value });
  };

  if (loading) {
    return (
      <Card fluid>
        <Card.Content>
          <Dimmer active><Loader content={locale.messages.loading} /></Dimmer>
        </Card.Content>
      </Card>
    );
  }

  if (!audit) {
    return (
      <Card fluid>
        <Card.Content>
          <Header as='h2'>{locale.details.messages.auditNotFound}</Header>
        </Card.Content>
      </Card>
    );
  }

  var renderStatusSection = function() {
    if (!auditStatuses || auditStatuses.length === 0) return null;

    var getStatusName = function(statusId) {
      if (!mainStatuses || mainStatuses.length === 0) return statusId;
      var found = mainStatuses.find(function(s) {
        return (s.inS_MainStatusID || s.INS_MainStatusID) === statusId;
      });
      return found ? (found.inS_MainStatusName || found.INS_MainStatusName) : statusId;
    };

    return (
      <Segment>
        <Header as='h3'>{locale.details.headers.status}</Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{locale.details.statusTable.headers.statusType}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.statusTable.headers.date}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.statusTable.headers.remarks}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {auditStatuses.map(function(status, idx) {
              var statusId = status.inS_MainStatusID || status.INS_MainStatusID;
              return (
                <Table.Row key={idx}>
                  <Table.Cell>{getStatusName(statusId)}</Table.Cell>
                  <Table.Cell>{formatDateTime(status.inS_AuditStatus_StatusDate || status.INS_AuditStatus_StatusDate) || ' '}</Table.Cell>
                  <Table.Cell>{status.inS_AuditStatus_StatusRemarks || status.INS_AuditStatus_StatusRemarks || ' '}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  var renderKpiSection = function() {
    if (!isKPIMng || !kpiData || kpiData.length === 0) return null;

    return (
      <Segment>
        <Header as='h3'>{locale.headers.kpi}</Header>
        <div style={{ overflowX: 'auto' }}>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>{locale.headers.kpi}</Table.HeaderCell>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>{locale.labels.executionLevel}</Table.HeaderCell>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>{locale.labels.followupAlert}</Table.HeaderCell>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>{locale.labels.followup}</Table.HeaderCell>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap', width: '1%' }}>{locale.details.labels.followupResultColumn}</Table.HeaderCell>
              <Table.HeaderCell style={{ whiteSpace: 'nowrap', width: '15%' }}>{locale.labels.followupDate}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {kpiData.map(function(kpi) {
              return (
                <Table.Row key={kpi.INS_KPITypeID}>
                  <Table.Cell style={{ width: '40%' }}>
                    <Thing>
                      <div>
                        {(kpi.kpiTypeName || '').split('\n').map(function(l, k) {
                          return <span key={k}>{l}<br /></span>;
                        })}
                      </div>
                      {kpi.INS_MainAuditKPIRemarks ? (
                        <Icon
                          className="kpi-icon"
                          name='info circle'
                          style={{ margin: 'auto 10px auto 0', cursor: 'pointer' }}
                          onClick={function() { openReadOnlyModal(locale.details.modals.remarks, kpi.INS_MainAuditKPIRemarks); }}
                        />
                      ) : null}
                    </Thing>
                    <Thing>
                      <Icon
                        className="kpi-icon"
                        name="question circle outline"
                        style={{ margin: 'auto 10px auto 0', cursor: 'pointer' }}
                        onClick={function() { openReadOnlyModal(locale.details.modals.description, kpi.kpiTypeDescription || locale.details.messages.noDescription); }}
                      />
                    </Thing>
                  </Table.Cell>
                  <Table.Cell style ={{ width: '20%'}}>
                    <EvalContainer>
                      <EvalButtonsContainer>
                        {kpiNames.map(function(kpiName) {
                          var isSelected = kpi.INS_MainCategoryKPI === kpiName.index;
                          return (
                            <EvalButton
                              key={kpiName.index}
                              type="button"
                              selected={isSelected}
                              disabled
                            >
                              {kpiName.name}
                            </EvalButton>
                          );
                        })}
                      </EvalButtonsContainer>
                    </EvalContainer>
                  </Table.Cell>
                  <Table.Cell textAlign='center' style={{ width: '7%' }}>
                    <Checkbox
                      checked={kpi.INS_MainAuditKPIFollowupAlert || false}
                      disabled
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="center" style={{ width: '7%' }}>
                    {kpi.INS_MainAuditKPIFollowup ? (
                      <Icon
                        className="kpi-icon"
                        name="info circle"
                        style={{ margin: '0 auto', cursor: 'pointer' }}
                        color='blue'
                        onClick={function() { openReadOnlyModal(locale.details.modals.followup, kpi.INS_MainAuditKPIFollowup); }}
                      />
                    ) : (
                      <Icon
                        className="kpi-icon"
                        name="edit"
                        style={{ margin: '0 auto' }}
                        color='grey'
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center" style={{ width: '7%' }}>
                    {kpi.INS_MainAuditKPIFollowupResult ? (
                      <Icon
                        className="kpi-icon"
                        name="info circle"
                        style={{ margin: '0 auto', cursor: 'pointer' }}
                        color='blue'
                        onClick={function() { openReadOnlyModal(locale.details.modals.followupResult, kpi.INS_MainAuditKPIFollowupResult); }}
                      />
                    ) : (
                      <Icon
                        className="kpi-icon"
                        name="edit"
                        style={{ margin: '0 auto' }}
                        color='grey'
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(kpi.INS_MainAuditKPIFollowupDate) || ' ' }</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        </div>
      </Segment>
    );
  };

  var renderEmployeesSection = function() {
    if (!auditPersons || auditPersons.length === 0) return null;

    return (
      <Segment>
        <Header as='h3'>{locale.headers.employees}</Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{locale.details.employeesTable.headers.name}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.employeesTable.headers.followupTaskType}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.employeesTable.headers.followupTaskSubject}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.employeesTable.headers.followupAlert}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.employeesTable.headers.followupRemarks}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.employeesTable.headers.followupResultDate}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {auditPersons.map(function(person) {
              return (
                <Table.Row key={person.RDS_Person_ID}>
                  <Table.Cell>{person.personName}</Table.Cell>
                  <Table.Cell>{person.TrainingEventType_Name || ' '}</Table.Cell>
                  <Table.Cell>{person.TrainingEventCategory_Name || ' '}</Table.Cell>
                  <Table.Cell textAlign='center'>
                    <Checkbox
                      checked={person.INS_MainAudit2PersonFollowupAlert || false}
                      disabled
                    />
                  </Table.Cell>
                  <Table.Cell>{person.INS_MainAudit2PersonFollowupRemarks || ' '}</Table.Cell>
                  <Table.Cell>{formatDateOnly(person.INS_MainAudit2PersonFollowupResultDate) || ' '}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  var renderFollowupsSection = function() {
    if (!isFollowupMng || !auditFollowups || auditFollowups.length === 0) return null;

    var getFollowupTypeName = function(typeId) {
      if (!followupTypes || !typeId) return ' ';
      var found = followupTypes.find(function(ft) {
        return (ft.inS_FollowupTypeID || ft.INS_FollowupTypeID) === typeId;
      });
      return found ? (found.inS_FollowupTypeName || found.INS_FollowupTypeName) : typeId;
    };

    return (
      <Segment>
        <Header as='h3'>{locale.headers.auditFollowups}</Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{locale.details.followupsTable.headers.actionItem}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.followupsTable.headers.startDate}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.followupsTable.headers.endDate}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.followupsTable.headers.remarks}</Table.HeaderCell>
              <Table.HeaderCell>{locale.details.followupsTable.headers.alert}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {auditFollowups.map(function(f, idx) {
              var typeId = f.inS_FollowupTypeID || f.INS_FollowupTypeID;
              return (
                <Table.Row key={idx}>
                  <Table.Cell>{getFollowupTypeName(typeId)}</Table.Cell>
                  <Table.Cell>{formatDateOnly(f.inS_MainAuditFollowupsStartDate || f.INS_MainAuditFollowupsStartDate) || ' '}</Table.Cell>
                  <Table.Cell>{formatDateOnly(f.inS_MainAuditFollowupsEndDate || f.INS_MainAuditFollowupsEndDate) || ' '}</Table.Cell>
                  <Table.Cell>{f.inS_MainAuditFollowupsRemarks || f.INS_MainAuditFollowupsRemarks || ' '}</Table.Cell>
                  <Table.Cell textAlign='center'>
                    <Checkbox checked={f.inS_MainAuditFollowupsAlert || f.INS_MainAuditFollowupsAlert || false} disabled />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  return (
    <Card.Group>
      <Card fluid>
        <Card.Content>
          <button type="button" onClick={function() { history.goBack(); }}>
            <Icon name="arrow left" />
          </button>
          <Card.Header>
            <Breadcrumb>
              <Breadcrumb.Section link as={Link} to="/audit">{locale.breadcrumb.audits}</Breadcrumb.Section>
              <Breadcrumb.Divider />
              <Breadcrumb.Section active>{locale.details.breadcrumb.auditDetails}</Breadcrumb.Section>
            </Breadcrumb>
          </Card.Header>

          <Segment>
            <Header as='h3'>{locale.headers.generalInformation}</Header>
            <ReadOnlyField>
                <label>{locale.labels.auditDate}</label>
                <div className="value">{formatDateTime(audit.inS_MainAuditDate) || ' '}</div>
              </ReadOnlyField>
            <FieldRow>
              <ReadOnlyField>
                <label>{locale.labels.unitName}</label>
                <div className="value">{audit.rdS_Unit_Name || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.labels.auditAreaName}</label>
                <div className="value">{audit.inS_MainCategoryName || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.labels.auditSubjectName}</label>
                <div className="value">{audit.inS_SubCategoryName || ' '}</div>
              </ReadOnlyField>
            </FieldRow>

            <FieldRow>
              <ReadOnlyField>
                <label>{locale.labels.followupManager}</label>
                <div className="value">{audit.inS_MainAuditFollowupManagerName || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.labels.auditorName}</label>
                <div className="value">{audit.inS_MainAuditUserName || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.labels.unitManager}</label>
                <div className="value">{audit.inS_MainAuditManagerName || ' '}</div>
              </ReadOnlyField>
            </FieldRow>

            <FieldRow>
              <ReadOnlyField>
                <label>{locale.labels.location}</label>
                <div className="value">{audit.inS_MainAuditLocation || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.details.labels.sublocation}</label>
                <div className="value">{audit.inS_MainAuditSubLocation || ' '}</div>
              </ReadOnlyField>
            </FieldRow>
          </Segment>

          {renderStatusSection()}

          {renderKpiSection()}

          <Segment>
            <Header as='h3'>{locale.headers.commentsFollowup}</Header>
            <ReadOnlyField>
              <label>{locale.details.labels.generalComments}</label>
              <div className="value">{audit.inS_MainAuditGeneralComments || ' '}</div>
            </ReadOnlyField>
            <ReadOnlyField>
              <label>{locale.labels.followupActionItems}</label>
              <div className="value">{audit.inS_MainAuditGeneralFollowup || ' '}</div>
            </ReadOnlyField>
            <FieldRow>
              <ReadOnlyField>
                <label>{locale.details.labels.followupResult}</label>
                <div className="value">{audit.inS_MainAuditGeneralFollowupResult || ' '}</div>
              </ReadOnlyField>
              <ReadOnlyField>
                <label>{locale.labels.completionTargetDate}</label>
                <div className="value">{formatDateTime(audit.inS_MainAuditGeneralFollowupDate) || ' '}</div>
              </ReadOnlyField>
            </FieldRow>
          </Segment>      

          {renderEmployeesSection()}
          {renderFollowupsSection()}
           

          <Segment>
            <Header as='h3'>{locale.details.headers.attachmentsSignatures}</Header>
            <DownloadSection>
              {audit.inS_MainAuditUploadFilename1 && (
                <div className="download-item">
                  <Icon name='file' />
                  <span className="filename">{audit.inS_MainAuditUploadFilename1}</span>
                  <Button
                    size='small'
                    primary
                    onClick={() => downloadFile('upload1', audit.inS_MainAuditUploadFilename1)}
                  >
                    <Icon name='download' /> {locale.details.buttons.download}
                  </Button>
                </div>
              )}
              {audit.inS_MainAuditUploadFilename2 && (
                <div className="download-item">
                  <Icon name='file' />
                  <span className="filename">{audit.inS_MainAuditUploadFilename2}</span>
                  <Button
                    size='small'
                    primary
                    onClick={() => downloadFile('upload2', audit.inS_MainAuditUploadFilename2)}
                  >
                    <Icon name='download' /> {locale.details.buttons.download}
                  </Button>
                </div>
              )}
              {audit.inS_MainAuditUploadFilename3 && (
                <div className="download-item">
                  <Icon name='file' />
                  <span className="filename">{audit.inS_MainAuditUploadFilename3}</span>
                  <Button
                    size='small'
                    primary
                    onClick={() => downloadFile('upload3', audit.inS_MainAuditUploadFilename3)}
                  >
                    <Icon name='download' /> {locale.details.buttons.download}
                  </Button>
                </div>
              )}
              {audit.inS_MainAuditManagerSig && (
                <div className="download-item">
                  <Icon name='pencil' />
                  <span className="filename">{locale.details.signatures.managerSignature}</span>
                  <Button
                    size='small'
                    primary
                    onClick={() => downloadFile('managersig', 'manager_signature.png')}
                  >
                    <Icon name='download' /> {locale.details.buttons.download}
                  </Button>
                </div>
              )}
              {audit.inS_MainAuditEmployeeSig && (
                <div className="download-item">
                  <Icon name='pencil' />
                  <span className="filename">{locale.details.signatures.employeeSignature}</span>
                  <Button
                    size='small'
                    primary
                    onClick={() => downloadFile('employeesig', 'employee_signature.png')}
                  >
                    <Icon name='download' /> {locale.details.buttons.download}
                  </Button>
                </div>
              )}
              {!audit.inS_MainAuditUploadFilename1 &&
               !audit.inS_MainAuditUploadFilename2 &&
               !audit.inS_MainAuditUploadFilename3 &&
               !audit.inS_MainAuditManagerSig &&
               !audit.inS_MainAuditEmployeeSig && (
                <div>{locale.details.messages.noAttachmentsAvailable}</div>
              )}
            </DownloadSection>
          </Segment>

          <ReadOnlyField style={{ marginTop: '1em' }}>
            <label>{locale.details.labels.lastUpdated}</label>
            <div className="value">{formatDateTime(audit.inS_MainAuditTimestamp) || '-'}</div>
          </ReadOnlyField>

          <div style={{ marginTop: '1.5em' }}>
            <Button as={Link} to="/audit">
              <Icon name='arrow left' /> {locale.details.buttons.backToAudits}
            </Button>
          </div>

        </Card.Content>
      </Card>

      {/* Read-only text modal */}
      <Modal
        open={readOnlyModal.open}
        onClose={function() { setReadOnlyModal({ open: false, title: '', value: '' }); }}
        size='small'
      >
        <Modal.Header>
          <Icon name='info circle' /> {readOnlyModal.title}
        </Modal.Header>
        <Modal.Content>
          <p>{readOnlyModal.value || ' '}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={function() { setReadOnlyModal({ open: false, title: '', value: '' }); }}>
            <Icon name='close' /> {locale._system.close}
          </Button>
        </Modal.Actions>
      </Modal>
    </Card.Group>
  );
};

export default compose(
  hot(module),
  withLocale('audit'),
  withAuditDetails
)(AuditDetails);
